import { useState } from 'react';
import { ActionIcon, Badge, Button, Group, Stack, Table, Text, TextInput } from '@mantine/core';
import { IconStar, IconStarFilled, IconTrash } from '@tabler/icons-react';
import { checkBankAccount, checkBik, normalizeRequisite } from '@erp/shared';

export interface BankAccountRow {
  id: string;
  accountNumber: string;
  bankName: string;
  bik: string;
  corrAccount: string;
  isDefault: boolean;
}

interface Props {
  accounts: BankAccountRow[];
  canEdit: boolean;
  onAdd: (data: { accountNumber: string; bankName: string; bik: string; corrAccount: string; isDefault: boolean }) => Promise<void>;
  onSetDefault: (id: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

const emptyForm = { accountNumber: '', bankName: '', bik: '', corrAccount: '' };

/**
 * Реквизиты банковских счетов проверяются теми же функциями (@erp/shared), что
 * и на сервере — чтобы сообщение об ошибке совпадало и не приходилось узнавать
 * об опечатке только после ответа API.
 */
export function BankAccountsEditor({ accounts, canEdit, onAdd, onSetDefault, onDelete }: Props) {
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleAdd() {
    setError(null);
    const bik = normalizeRequisite(form.bik);
    const accountNumber = normalizeRequisite(form.accountNumber);
    const corrAccount = normalizeRequisite(form.corrAccount);

    if (!form.bankName.trim()) return setError('Укажите наименование банка');

    const bikCheck = checkBik(bik);
    if (!bikCheck.valid) return setError(`БИК: ${bikCheck.error}`);

    const accountCheck = checkBankAccount(accountNumber, bik);
    if (!accountCheck.valid) return setError(`Расчётный счёт: ${accountCheck.error}`);

    const corrCheck = checkBankAccount(corrAccount, bik);
    if (!corrCheck.valid) return setError(`Корр. счёт: ${corrCheck.error}`);

    setBusy(true);
    try {
      await onAdd({
        accountNumber,
        bankName: form.bankName.trim(),
        bik,
        corrAccount,
        isDefault: accounts.length === 0,
      });
      setForm(emptyForm);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось добавить счёт');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap="sm">
      {accounts.length > 0 && (
        <Table verticalSpacing={4}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Расчётный счёт</Table.Th>
              <Table.Th>Банк</Table.Th>
              <Table.Th>БИК</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {accounts.map((acc) => (
              <Table.Tr key={acc.id}>
                <Table.Td>
                  <Group gap={6}>
                    <Text size="sm">{acc.accountNumber}</Text>
                    {acc.isDefault && (
                      <Badge size="xs" variant="light">
                        по умолчанию
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{acc.bankName}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{acc.bik}</Text>
                </Table.Td>
                <Table.Td>
                  {canEdit && (
                    <Group gap={4} justify="flex-end" wrap="nowrap">
                      <ActionIcon
                        variant="subtle"
                        color={acc.isDefault ? 'yellow' : 'gray'}
                        disabled={acc.isDefault}
                        title="Сделать счётом по умолчанию"
                        onClick={() => void onSetDefault(acc.id)}
                      >
                        {acc.isDefault ? <IconStarFilled size={16} /> : <IconStar size={16} />}
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        title="Удалить счёт"
                        onClick={() => void onDelete(acc.id)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {canEdit && (
        <Stack gap={6}>
          <Group grow>
            <TextInput
              placeholder="Расчётный счёт, 20 цифр"
              value={form.accountNumber}
              onChange={(e) => setForm({ ...form, accountNumber: e.currentTarget.value })}
            />
            <TextInput
              placeholder="БИК"
              value={form.bik}
              onChange={(e) => setForm({ ...form, bik: e.currentTarget.value })}
            />
          </Group>
          <Group grow>
            <TextInput
              placeholder="Наименование банка"
              value={form.bankName}
              onChange={(e) => setForm({ ...form, bankName: e.currentTarget.value })}
            />
            <TextInput
              placeholder="Корр. счёт"
              value={form.corrAccount}
              onChange={(e) => setForm({ ...form, corrAccount: e.currentTarget.value })}
            />
          </Group>
          {error && (
            <Text size="xs" c="red">
              {error}
            </Text>
          )}
          <Button size="xs" variant="light" onClick={() => void handleAdd()} loading={busy} w="fit-content">
            Добавить счёт
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
